import React, { useState, useEffect } from "react";
import axios from "axios";
import jwtDecode from "jwt-decode";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Button,
  Dialog,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { appUrl } from "../../../../../../utils/config";
import { useApiBaseUrl } from "../../CommonMethods/CommonMethods";
import { FIELD_TYPE_OPTIONS, INGESTION_OPTIONS } from "./erpToErpConfigData";

// Current logged-in user's SaaS user ID, read fresh from the auth token at save time
// (same jwtDecode(localStorage authToken) pattern already used elsewhere in this
// codebase, e.g. DocumentResubmissionDialog.jsx).
const getSaasUserID = () => {
  try {
    const token = localStorage.getItem("authToken");
    return token ? jwtDecode(token)?.saas_user_id ?? null : null;
  } catch (err) {
    console.error("Error decoding authToken for saasUserID:", err);
    return null;
  }
};

const useStyles = makeStyles((theme) => ({
  modalRoot: {
    fontFamily: theme.typography.fontFamily,
  },
  modalHeader: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #eee",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#000",
    textAlign: "center",
  },
  modalCancelX: {
    position: "absolute",
    top: "50%",
    right: theme.spacing(3),
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: "#000",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
  },
  cancelIcon: {
    color: "#e53935",
  },
  modalBody: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  modalInfoBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(4),
    backgroundColor: "#eef3fb",
    borderRadius: 6,
    padding: theme.spacing(2, 3),
  },
  infoLabel: {
    fontSize: 11,
    color: "#888",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000",
  },
  requiredStar: {
    color: "#e53935",
  },
  viewTable: {
    border: "1px solid #ddd",
    minWidth: 700,
  },
  viewHeaderCell: {
    backgroundColor: "#fff",
    color: "#000",
    fontWeight: 600,
    fontSize: 12,
    padding: "8px 10px",
    whiteSpace: "nowrap",
  },
  viewBodyCell: {
    fontSize: 12,
    padding: "8px 10px",
    verticalAlign: "top",
  },
  boldValueCell: {
    fontWeight: 700,
    color: "#1890FF",
  },
  viewTableWrapper: {
    maxHeight: 420,
    overflow: "auto",
  },
  errorText: {
    color: "#c62828",
    fontSize: 13,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #eee",
  },
  modalCancelBtn: {
    border: "1px solid #ccc",
    color: "#000",
    borderRadius: 4,
    padding: "6px 20px",
    textTransform: "none",
  },
  modalSaveBtn: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 4,
    padding: "6px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  compactSelect: {
    minWidth: 130,
    fontSize: 12,
  },
  na: {
    color: "#888",
    fontStyle: "italic",
  },
}));

const canSaveIngestion = (rows) => rows.length > 0 && rows.every((r) => r.segment.trim() && r.field.trim());

// Reusable "Configure IDoc Ingestion" popup for inbound Module Messages rows — mirrors
// the ConfigureIDocTriggerPointDialog pattern in this same codebase: fetches its own
// seed data (Data Elements) and existing config (fetchMessageParameters) while open, and
// only reports the result back via onSave — the caller updates its own local state
// (moduleMessages) and closes the dialog once that succeeds.
const ConfigureIDocIngestionDialog = ({
  open,
  onClose,
  onSave,
  messageInfo, // { name, buildNo, description }
  collabrixMessageID,
  collabrixVendorMessageID,
  vendorMosymphonyID,
  brandOwnerMosymphonyID,
  cmoName,
  productType,
  initialRows = [],
  // Attributes of the underlying parameters file this ingestion configuration is based
  // on. Not yet wired up from the parent (which doesn't parse/track these today) — default
  // to sensible values so /updateIngestionConfiguration always gets a well-formed payload;
  // pass real values once the caller has them (e.g. from parsing the Excel parameters file).
  fileHasMultipleTabs = false,
  headerRowNumber = 1,
}) => {
  const classes = useStyles();
  const API_BASE_URL = useApiBaseUrl(appUrl);

  const [ingestionDraft, setIngestionDraft] = useState([]);
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [ingestionSaving, setIngestionSaving] = useState(false);
  const [ingestionSaveError, setIngestionSaveError] = useState("");
  const [dataElements, setDataElements] = useState(null);
  // Config-level ID: null for a brand-new configuration, or the existing
  // ingestionConfigID once fetchMessageParameters returns one for this message.
  const [ingestionConfigID, setIngestionConfigID] = useState(null);

  // Cross-references a row's dataElementId (from /fetchMessageParameters) against the
  // /fetchDataElements list to display that data element's label — e.g. dataElementId 7
  // resolves to dataElementLabel "CMO UOM". Returns "" (never undefined) while
  // dataElements hasn't loaded yet, or if the ID isn't found (e.g. 0 = unmapped).
  const getDataElementLabel = (dataElementId) =>
    (dataElements || []).find((el) => el.dataElementId === dataElementId)?.dataElementLabel || "";

  // Seed the draft from whatever's already saved locally, and reset any stale error,
  // every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setIngestionDraft((initialRows || []).map((r) => ({ ...r })));
    setIngestionSaveError("");
    setIngestionConfigID(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Data Elements seed data — drives the "Data Elements" dropdown.
  useEffect(() => {
    if (!open || !API_BASE_URL) return;
    axios
      .get(`${API_BASE_URL}/fetchDataElements`)
      .then((response) => {
        const ok = response?.data?.status === 200;
        const list = ok ? response?.data?.data?.dataElementsList : null;
        setDataElements((list || []).filter((el) => !el.markForDelete));
      })
      .catch((err) => {
        console.error("Error fetching data elements:", err);
        setDataElements([]);
      });
  }, [open, API_BASE_URL]);

  // Live message parameters, when reachable — replaces the locally-seeded draft. A
  // fetch failure or empty response just leaves the local seed in place.
  useEffect(() => {
    if (!open || !API_BASE_URL || !collabrixMessageID || !vendorMosymphonyID) return;
    let cancelled = false;
    setIngestionLoading(true);
    axios
      .get(`${API_BASE_URL}/fetchMessageParameters/${collabrixMessageID}/${vendorMosymphonyID}`)
      .then((response) => {
        if (cancelled) return;
        // This endpoint returns one of two shapes depending on whether this message has
        // ever been saved via /updateIngestionConfiguration yet: before the first save,
        // rows come back under messageParametersList (no ingestionConfigId yet — a brand
        // new configuration); after a save, the same rows come back under columnMappings,
        // alongside the now-existing ingestionConfigId. Both carry rows with the same
        // per-item field names, so they're mapped identically below.
        const list = Array.isArray(response?.data?.messageParametersList)
          ? response.data.messageParametersList
          : Array.isArray(response?.data?.columnMappings)
          ? response.data.columnMappings
          : [];
        if (response?.data?.ingestionConfigId != null) {
          setIngestionConfigID(response.data.ingestionConfigId);
        }
        if (list.length > 0) {
          setIngestionDraft(
            list.map((item, idx) => ({
              rowId: item.ingestionConfigColumnId ?? `ing_${idx}`,
              ingestionConfigColumnId: item.ingestionConfigColumnId ?? null,
              segment: item.segment || "",
              conditionalElement: item.conditionalElement || "",
              field: item.technicalFieldName || "",
              fieldType: item.fieldType || "",
              // dataElementId is the join key back to /fetchDataElements — the row only ever
              // carries the ID; the label shown in the table is resolved at render time via
              // getDataElementLabel() once the Data Elements list has loaded.
              dataElementId: item.dataElementId ?? null,
              columnName: item.columnName || "",
              ingestion: item.ingestion || "",
            }))
          );
        }
      })
      .catch((err) => {
        console.error(
          `Error fetching message parameters (collabrixMessageID=${collabrixMessageID}, vendorMosymphonyID=${vendorMosymphonyID}):`,
          err
        );
      })
      .finally(() => {
        if (!cancelled) setIngestionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, API_BASE_URL, collabrixMessageID, vendorMosymphonyID]);

  const updateIngestionRow = (rowId, field, value) => {
    setIngestionDraft((prev) =>
      prev.map((r) => {
        if (r.rowId !== rowId) return r;
        const updated = { ...r, [field]: value };
        // Only force Ingestion to "Mandatory" when Field Type itself is the field being
        // changed (and changed to "Chain of Custody") — otherwise this would clobber a
        // direct Ingestion selection right back to its old value.
        if (field === "fieldType" && value === "Chain of Custody") {
          updated.ingestion = "Mandatory";
        }
        return updated;
      })
    );
  };

  const handleSave = async () => {
    if (!canSaveIngestion(ingestionDraft) || ingestionSaving) return;
    if (!API_BASE_URL) {
      setIngestionSaveError("Unable to reach the server. Please try again.");
      return;
    }

    const columnMappings = ingestionDraft.map((r) => ({
      ingestionConfigColumnId: r.ingestionConfigColumnId ?? 0,
      columnName: r.columnName || "",
      dataElementId: r.dataElementId ?? null,
      fieldType: r.fieldType,
      ingestion: r.ingestion,
      segment: r.segment,
      conditionalElement: r.conditionalElement,
      technicalFieldName: r.field,
      markForDelete: false,
    }));

    const payload = {
      ingestionConfigurations: [
        {
          ingestionConfigId: ingestionConfigID ?? 0,
          category: "Inbound Idoc Configuration",
          ingestThrough: "IDOC",
          collabrixMessageID: collabrixMessageID ?? null,
          vendorMosymphonyId: vendorMosymphonyID ?? null,
          fileHasMultipleTabs,
          headerRowNumber,
          saasUserID: getSaasUserID(),
          columnMappings,
        },
      ],
    };

    setIngestionSaving(true);
    setIngestionSaveError("");
    try {
      const response = await axios.post(`${API_BASE_URL}/updateIngestionConfiguration`, payload);
      const ok = response?.data?.status === true || response?.data?.status === 200;
      if (!ok) {
        setIngestionSaveError(response?.data?.message || "Failed to save the ingestion configuration.");
        return;
      }
      onSave(ingestionDraft);
    } catch (err) {
      console.error(
        `Error saving ingestion configuration (collabrixMessageID=${collabrixMessageID}, vendorMosymphonyID=${vendorMosymphonyID}):`,
        err
      );
      setIngestionSaveError(err?.response?.data?.message || "An error occurred while saving. Please try again.");
    } finally {
      setIngestionSaving(false);
    }
  };

  return (
    <Dialog open={!!(open && messageInfo)} onClose={onClose} maxWidth="lg" fullWidth>
      {messageInfo && (
        <div className={classes.modalRoot}>
          <div className={classes.modalHeader}>
            <span className={classes.modalTitle}>Configure IDoc Ingestion</span>
            <div className={classes.modalCancelX} onClick={onClose}>
              Cancel <CloseIcon fontSize="small" className={classes.cancelIcon} />
            </div>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.modalInfoBox}>
              <div>
                <div className={classes.infoLabel}>Message</div>
                <div className={classes.infoValue}>{messageInfo.name}</div>
              </div>
              <div>
                <div className={classes.infoLabel}>Build-Version No.</div>
                <div className={classes.infoValue}>{messageInfo.buildNo}</div>
              </div>
              <div>
                <div className={classes.infoLabel}>Description</div>
                <div className={classes.infoValue}>{messageInfo.description}</div>
              </div>
              <div>
                <div className={classes.infoLabel}>CMO</div>
                <div className={classes.infoValue}>{cmoName || "—"}</div>
              </div>
              <div>
                <div className={classes.infoLabel}>Product Type</div>
                <div className={classes.infoValue}>{productType || "—"}</div>
              </div>
            </div>

            <div className={classes.viewTableWrapper} style={{ maxHeight: 320 }}>
              <Table className={classes.viewTable} style={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.viewHeaderCell}>
                      Segment<span className={classes.requiredStar}>*</span>
                    </TableCell>
                    <TableCell className={classes.viewHeaderCell}>Conditional Element</TableCell>
                    <TableCell className={classes.viewHeaderCell}>
                      Field (Technical)<span className={classes.requiredStar}>*</span>
                    </TableCell>
                    <TableCell className={classes.viewHeaderCell}>
                      Data Elements<span className={classes.requiredStar}>*</span>
                    </TableCell>
                    <TableCell className={classes.viewHeaderCell}>
                      Field Type<span className={classes.requiredStar}>*</span>
                    </TableCell>
                    <TableCell className={classes.viewHeaderCell}>
                      Ingestion<span className={classes.requiredStar}>*</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ingestionLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} style={{ textAlign: "center", padding: "48px 0", border: "none" }}>
                        <CircularProgress size={28} color="primary" />
                      </TableCell>
                    </TableRow>
                  ) : (
                  ingestionDraft.map((row) => (
                    <TableRow key={row.rowId}>
                      <TableCell className={`${classes.viewBodyCell} ${classes.boldValueCell}`}>{row.segment || "—"}</TableCell>
                      <TableCell className={classes.viewBodyCell}>{row.conditionalElement || "—"}</TableCell>
                      <TableCell className={`${classes.viewBodyCell} ${classes.boldValueCell}`}>{row.field || "—"}</TableCell>
                      <TableCell className={classes.viewBodyCell}>{getDataElementLabel(row.dataElementId) || "—"}</TableCell>
                      <TableCell className={classes.viewBodyCell}>
                        <Select
                          className={classes.compactSelect}
                          displayEmpty
                          value={row.fieldType}
                          onChange={(e) => updateIngestionRow(row.rowId, "fieldType", e.target.value)}
                        >
                          <MenuItem value="" disabled>
                            Select
                          </MenuItem>
                          {FIELD_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell className={classes.viewBodyCell}>
                        <Select
                          className={classes.compactSelect}
                          displayEmpty
                          disabled={row.fieldType === "Chain of Custody"}
                          value={row.ingestion}
                          onChange={(e) => updateIngestionRow(row.rowId, "ingestion", e.target.value)}
                        >
                          <MenuItem value="" disabled>
                            Select
                          </MenuItem>
                          {INGESTION_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </div>
            {ingestionSaveError && <div className={classes.errorText}>{ingestionSaveError}</div>}
          </div>
          <div className={classes.modalFooter}>
           
            <Button
              className={classes.modalSaveBtn}
              disabled={!canSaveIngestion(ingestionDraft) || ingestionSaving}
              style={{ opacity: !canSaveIngestion(ingestionDraft) || ingestionSaving ? 0.5 : 1 }}
              onClick={handleSave}
              startIcon={ingestionSaving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
            >
              {ingestionSaving ? "Saving..." : "Save ›"}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ConfigureIDocIngestionDialog;
